import React from 'react';
import { Alert } from 'reactstrap';
import { AlertTriangle, Smartphone, Activity, Battery, Bluetooth } from 'lucide-react';

const WarningAlert = ({ devices, cameras, selectedSubject, noSubjects }) => {
  // Utility function to check battery saving mode
  const isAnyDeviceInBatterySavingMode = () => {
    return devices.some(device => device.data?.deviceInfo?.isBatterySaverModeActive);
  };

  // Utility function to check device space
  const hasEnoughSpace = () => {
    const phoneDevicesCheck = devices.some(
      device =>
        device.data === null ||
        (device?.deviceName?.toLowerCase()?.includes("iphone") &&
          device?.data?.deviceInfo?.remainingSpace > 2048) ||
        device?.data?.deviceInfo?.remainingSpace > 5120
    );

    const cameraDevicesCheck = cameras.every(camera =>
      camera.disk.freesize > camera.disk.totalsize * 0.05
    );

    return (cameras.length > 0 && cameraDevicesCheck) ||
      (devices.length > 0 && phoneDevicesCheck);
  };

  // Utility function to check bluetooth
  const hasBluetoothEnabled = () => {
    return devices.some(device => device.bleDevice === "On");
  };

  const getWarningMessage = () => {
    if (devices.length + cameras.length === 0) {
      return {
        message: "Please connect a device to start a recording",
        icon: <Smartphone className="me-2" size={18} />,
        hint: "Connect your mobile device or camera to begin recordings."
      };
    }

    if (noSubjects || !selectedSubject) {
      return {
        message: "Please create and select a subject to start a recording",
        icon: <Activity className="me-2" size={18} />,
        hint: "Create a new patient profile or select an existing one."
      };
    }

    if (isAnyDeviceInBatterySavingMode()) {
      return {
        message: "One or more devices is in battery saving mode",
        icon: <Battery className="me-2" size={18} />,
        hint: "Please disable battery saving mode on all connected devices to proceed with the recording."
      };
    }

    if (!hasEnoughSpace()) {
      return {
        message: "One or more devices does not have enough space",
        icon: <AlertTriangle className="me-2" size={18} />,
        hint: "Free up space on your connected devices to ensure successful recording."
      };
    }

    if (process.env.REACT_APP_ENV_RESOURCE === 'dca-master' && !hasBluetoothEnabled()) {
      return {
        message: "At least one device must have Bluetooth enabled",
        icon: <Bluetooth className="me-2" size={18} />,
        hint: "Enable Bluetooth on at least one connected device to proceed."
      };
    }

    return null;
  };

  const warning = getWarningMessage();
  
  // Export all check functions
  const checks = {
    isAnyDeviceInBatterySavingMode,
    hasEnoughSpace,
    hasBluetoothEnabled,
    hasWarning: !!warning
  };

  if (!warning) return { component: null, checks };

  return {
    component: (
        <Alert
        color="warning"
        className="mt-3 mb-0"
        style={{
          backgroundColor: 'rgba(255, 237, 213, 0.6)',
          border: '1px solid rgb(254, 215, 170)',
          borderLeft: '4px solid rgb(234, 88, 12)'
        }}
      >
        <div className="d-flex align-items-center">
          {warning.icon}
          <div>
            <div className="fw-semibold">{warning.message}</div>
            <small className="text-muted">{warning.hint}</small>
          </div>
        </div>
      </Alert>
    ),
    checks
  };
};

export default WarningAlert;